import { useState, useEffect, createContext, useContext, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import LandingPage from "@/pages/LandingPage";
import Dashboard from "@/pages/Dashboard";
import History from "@/pages/History";
import Admin from "@/pages/Admin";
import Tableau from "@/pages/Tableau";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Use relative URL for API calls — works on any domain (preview, production, custom)
export const API = '/api';

// --- Auth state management (module-level to avoid race conditions) ---
let _isVerifyingAuth = false;
let _lastAuthCheck = 0;
const AUTH_CHECK_COOLDOWN = 5000; // Don't re-verify within 5s

// Authenticated fetch — adds Bearer token, handles 401 silently
export const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("session_token");
  if (token) {
    const existingHeaders = options.headers || {};
    const headerObj = existingHeaders instanceof Headers
      ? Object.fromEntries(existingHeaders.entries())
      : existingHeaders;
    options.headers = { ...headerObj, Authorization: `Bearer ${token}` };
  }
  delete options.credentials;
  return fetch(url, options);
};

// Keep for backward compatibility
export const getAuthHeaders = () => {
  const token = localStorage.getItem("session_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider — resilient against transient 401s
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const heartbeatRef = useRef(null);
  const userRef = useRef(null);

  // Keep userRef in sync
  useEffect(() => { userRef.current = user; }, [user]);

  // Core auth verification with retry logic
  const checkAuth = useCallback(async (isHeartbeat = false) => {
    const token = localStorage.getItem("session_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    // Prevent concurrent verifications
    if (_isVerifyingAuth) return;
    _isVerifyingAuth = true;

    const maxRetries = isHeartbeat ? 1 : 3;
    let lastError = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const response = await fetch(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          _lastAuthCheck = Date.now();
          _isVerifyingAuth = false;
          setLoading(false);
          return;
        }

        if (response.status === 401) {
          // Before logging out, verify the server is actually reachable
          // A 401 from an ingress/proxy (when backend is restarting) is NOT a real auth failure
          try {
            const pingRes = await fetch(`${API}/ping`);
            if (!pingRes.ok) {
              // Server is having issues — don't logout, treat as transient
              lastError = 'Server unreachable during 401';
              if (attempt < maxRetries - 1) {
                await new Promise(r => setTimeout(r, 2000));
                continue;
              }
              break; // Keep user logged in
            }
          } catch {
            // Ping failed — server is down, don't logout
            lastError = 'Ping failed';
            break;
          }

          // Server is up AND returned 401 — this is a real auth rejection
          if (isHeartbeat && attempt === 0) {
            await new Promise(r => setTimeout(r, 3000));
            continue;
          }
          // Confirmed real 401: clear session
          localStorage.removeItem("session_token");
          setUser(null);
          _isVerifyingAuth = false;
          setLoading(false);
          return;
        }

        // 5xx or other: don't logout, just keep current state
        lastError = `HTTP ${response.status}`;
      } catch (e) {
        lastError = e.message;
      }

      // Wait before retrying (exponential backoff)
      if (attempt < maxRetries - 1) {
        await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
      }
    }

    // All retries failed with network/5xx errors — keep user logged in
    _isVerifyingAuth = false;
    setLoading(false);
  }, []);

  // Initial auth check on mount
  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    // window.__HAS_AUTH_CALLBACK is set synchronously by inline script in index.html
    if (window.__HAS_AUTH_CALLBACK) {
      setLoading(false);
      return;
    }
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    let hasStoredSid = false;
    try { hasStoredSid = !!sessionStorage.getItem('__of_sid'); } catch(e) {}
    if (hasStoredSid) {
      setLoading(false);
      return;
    }
    checkAuth(false);
  }, [checkAuth]);

  // Silent heartbeat — re-verify session every 5 minutes without forcing logout
  useEffect(() => {
    heartbeatRef.current = setInterval(() => {
      const token = localStorage.getItem("session_token");
      if (token && userRef.current) {
        checkAuth(true);
      }
    }, 5 * 60 * 1000);
    return () => clearInterval(heartbeatRef.current);
  }, [checkAuth]);

  const login = (userData) => {
    setUser(userData);
    _lastAuthCheck = Date.now();
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        headers: getAuthHeaders(),
      });
    } catch (error) {
      // Logout call failed — still clear local state
    }
    localStorage.removeItem("session_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const hasProcessed = useRef(false);
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Read session_id from sessionStorage (set by inline script in index.html)
      let sessionId = null;
      try { sessionId = sessionStorage.getItem('__of_sid'); } catch(e) {}
      
      if (!sessionId) {
        const hash = window.location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        sessionId = sessionIdMatch?.[1];
      }
      
      // DON'T remove from sessionStorage yet — keep it so AppRouter
      // continues showing AuthCallback during re-renders
      
      if (!sessionId) {
        // Clean up flags
        window.__HAS_AUTH_CALLBACK = false;
        try { sessionStorage.removeItem('__of_sid'); } catch(e) {}
        setErrorMsg("Aucun identifiant de session. Veuillez réessayer.");
        setTimeout(() => navigate("/"), 3000);
        return;
      }

      // Retry session exchange up to 5 times (520 errors are transient proxy issues)
      for (let attempt = 0; attempt < 5; attempt++) {
        try {
          const response = await fetch(`${API}/auth/session`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ session_id: sessionId }),
          });

          if (response.ok) {
            const userData = await response.json();
            if (userData.session_token) {
              localStorage.setItem("session_token", userData.session_token);
            }
            // Clean up AFTER successful exchange
            window.__HAS_AUTH_CALLBACK = false;
            try { sessionStorage.removeItem('__of_sid'); } catch(e) {}
            login(userData);
            navigate("/tableau", { replace: true });
            return;
          }

          const errBody = await response.json().catch(() => ({}));
          const detail = errBody.detail || `Erreur ${response.status}`;

          if (response.status === 403) {
            window.__HAS_AUTH_CALLBACK = false;
            try { sessionStorage.removeItem('__of_sid'); } catch(e) {}
            setErrorMsg(detail);
            setTimeout(() => navigate("/"), 5000);
            return;
          }

          // Transient error (520, 502, 503, etc.) — retry with increasing delay
          if (attempt < 4) {
            await new Promise(r => setTimeout(r, 2000 * (attempt + 1)));
            continue;
          }

          window.__HAS_AUTH_CALLBACK = false;
          try { sessionStorage.removeItem('__of_sid'); } catch(e) {}
          setErrorMsg(`Échec de connexion : ${detail}`);
          setTimeout(() => navigate("/"), 4000);
          return;
        } catch (error) {
          if (attempt < 4) {
            await new Promise(r => setTimeout(r, 2000 * (attempt + 1)));
            continue;
          }
          window.__HAS_AUTH_CALLBACK = false;
          try { sessionStorage.removeItem('__of_sid'); } catch(e) {}
          setErrorMsg("Erreur réseau. Vérifiez votre connexion internet.");
          setTimeout(() => navigate("/"), 4000);
          return;
        }
      }
    };

    processAuth();
  }, [navigate, login]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50">
      <div className="text-center">
        {errorMsg ? (
          <>
            <div className="w-10 h-10 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
              <span className="text-red-500 text-lg font-bold">!</span>
            </div>
            <p className="text-red-600 font-medium mb-2">{errorMsg}</p>
            <p className="text-zinc-400 text-sm">Redirection en cours...</p>
          </>
        ) : (
          <>
            <div className="spinner mx-auto mb-4" style={{ width: 40, height: 40 }}></div>
            <p className="text-zinc-600">Connexion en cours...</p>
          </>
        )}
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="spinner" style={{ width: 40, height: 40 }}></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return children;
};

// App Router
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id - synchronous check prevents race conditions
  // Check sessionStorage (set by inline script before emergent-main.js)
  // Check window.__HAS_AUTH_CALLBACK (set by inline script, persists during re-renders)
  let hasStoredSessionId = false;
  try { hasStoredSessionId = !!sessionStorage.getItem('__of_sid'); } catch(e) {}
  
  if (location.hash?.includes("session_id=") || hasStoredSessionId || window.__HAS_AUTH_CALLBACK) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/history"
        element={
          <ProtectedRoute>
            <History />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <Admin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/tableau"
        element={
          <ProtectedRoute>
            <Tableau />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
